// ─── BattleViewer2D – Particle System ────────────────────────────────────────

import {
  Particle,
  CANVAS_W, CANVAS_H, ARENA_INSET,
  AMBIENT_PARTICLE_COUNT,
} from './types';

export function createAmbientParticle(): Particle {
  const isEmber = Math.random() < 0.35;
  return {
    x: ARENA_INSET + Math.random() * (CANVAS_W - ARENA_INSET * 2),
    y: ARENA_INSET + Math.random() * (CANVAS_H - ARENA_INSET * 2),
    vx: (Math.random() - 0.5) * 12,
    vy: isEmber ? -(8 + Math.random() * 18) : (Math.random() - 0.5) * 6,
    life: 2 + Math.random() * 4,
    maxLife: 6,
    size: isEmber ? 1.5 + Math.random() * 2 : 1 + Math.random() * 1.5,
    color: isEmber
      ? `hsl(${20 + Math.random() * 20}, 100%, ${55 + Math.random() * 20}%)`
      : `rgba(180,170,150,${0.15 + Math.random() * 0.2})`,
    type: isEmber ? 'ember' : 'dust',
    opacity: 0.3 + Math.random() * 0.5,
  };
}

export function initAmbientParticles(): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < AMBIENT_PARTICLE_COUNT; i++) {
    const p = createAmbientParticle();
    p.life = Math.random() * p.maxLife;
    particles.push(p);
  }
  return particles;
}

export function spawnImpactBurst(x: number, y: number, count: number, color: string): Particle[] {
  const out: Particle[] = [];
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 40 + Math.random() * 80;
    out.push({
      x, y,
      vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
      life: 0.3 + Math.random() * 0.3, maxLife: 0.6,
      size: 2 + Math.random() * 2, color, type: 'impact', opacity: 1,
    });
  }
  return out;
}

export function spawnSparkBurst(x: number, y: number): Particle[] {
  const out: Particle[] = [];
  const count = 4 + Math.floor(Math.random() * 4);
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 50 + Math.random() * 60;
    out.push({
      x, y,
      vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
      life: 0.2 + Math.random() * 0.25, maxLife: 0.45,
      size: 1.5 + Math.random() * 1.5, color: '#ffe4a0', type: 'spark', opacity: 1,
    });
  }
  return out;
}

export function spawnBleedDrip(x: number, y: number): Particle {
  return {
    x: x + (Math.random() - 0.5) * 8, y,
    vx: (Math.random() - 0.5) * 6, vy: 20 + Math.random() * 30,
    life: 1.0 + Math.random() * 1.5, maxLife: 2.5,
    size: 2 + Math.random(), color: '#cc2222', type: 'bleed', opacity: 0.9,
  };
}

export function spawnHealParticles(x: number, y: number): Particle[] {
  const out: Particle[] = [];
  for (let i = 0; i < 8; i++) {
    const angle = Math.random() * Math.PI * 2;
    out.push({
      x: x + (Math.random() - 0.5) * 16, y: y + (Math.random() - 0.5) * 16,
      vx: Math.cos(angle) * 15, vy: -(20 + Math.random() * 25),
      life: 0.8 + Math.random() * 0.6, maxLife: 1.4,
      size: 2.5 + Math.random() * 2, color: '#ffd700', type: 'heal', opacity: 0.95,
    });
  }
  return out;
}

export function spawnTrailParticle(x: number, y: number, color: string): Particle {
  return {
    x, y,
    vx: (Math.random() - 0.5) * 4, vy: (Math.random() - 0.5) * 4,
    life: 0.2 + Math.random() * 0.15, maxLife: 0.35,
    size: 2 + Math.random() * 2, color, type: 'trail', opacity: 0.7,
  };
}

export function updateParticles(particles: Particle[], dt: number): Particle[] {
  const alive: Particle[] = [];
  for (const p of particles) {
    p.life -= dt;
    if (p.life <= 0) {
      if (p.type === 'dust' || p.type === 'ember') { alive.push(createAmbientParticle()); }
      continue;
    }
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    if (p.type === 'bleed') p.vy += 60 * dt;
    if (p.type === 'impact' || p.type === 'spark' || p.type === 'trail') {
      p.vx *= 0.92; p.vy *= 0.92;
    }
    const lifeFrac = p.life / p.maxLife;
    if (lifeFrac < 0.4) p.opacity = (lifeFrac / 0.4) * (p.type === 'ember' ? 0.7 : 1);
    alive.push(p);
  }
  return alive;
}

export function drawParticles(ctx: CanvasRenderingContext2D, particles: Particle[]) {
  for (const p of particles) {
    ctx.save();
    ctx.globalAlpha = Math.max(0, p.opacity);
    if (p.type === 'ember') {
      ctx.shadowColor = p.color; ctx.shadowBlur = 6;
    } else if (p.type === 'spark') {
      ctx.shadowColor = '#fff'; ctx.shadowBlur = 4;
    } else if (p.type === 'heal') {
      ctx.shadowColor = '#ffd700'; ctx.shadowBlur = 8;
    }
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}
